/** Render pipeline for the Tier 1 layer document.
 *
 *  Two paths:
 *    1. **Fast path** — the document has no groups, no masks, and
 *       no adjustment layers. Plain Fabric rendering is correct
 *       and fastest. Tier 0 documents always hit this path, so
 *       there is zero perf impact for users not using Tier 1
 *       features. `renderDocument` returns immediately on this
 *       path; Fabric's own loop already painted the leaves.
 *    2. **Slow path** — at least one slow-path feature is present.
 *       Phase 2 handles **adjustment layers** in full; mask
 *       feather/density/inverted/disabled and non-passthrough
 *       groups continue to no-op with a dev warn until later
 *       phases ship. The adjustment compositor walks the tree
 *       back-to-front maintaining a running composite. Each
 *       rasterizable leaf is painted into the composite via the
 *       caller-supplied `paintLeaf`; each adjustment reads the
 *       composite, applies its config, writes the result to the
 *       adjustment's backing canvas (sourced via
 *       `getAdjustmentBacking`), and blits that back onto the
 *       running composite so subsequent adjustments see the
 *       cumulative effect.
 *
 *  AI tools' "flatten visible" path will use this same pipeline
 *  transparently — no AI surface changes when more slow-path
 *  features land. */

import { applyAdjustmentToCanvas, isImplementedKind } from './adjustments/dispatch';
import { traverseDescendants } from './layer-tree';
import type { EditorDocument, LayerNode } from '../types';

export interface RenderPipelineOptions {
  /** Source-of-truth layer tree. Pipeline walks this. */
  document: EditorDocument;
  /** Canvas dimensions in scene-space pixels. Used to size the
   *  running composite. Should match the editor's canvas
   *  dimensions; adjustment backing canvases are expected to match. */
  width: number;
  height: number;
  /** Paint one leaf layer's pixels (with its current transform)
   *  onto the target's 2d context. Caller knows how to read
   *  Fabric objects, paint surfaces, and apply transforms. No-op
   *  when the leaf has no rasterizable content (text/shape v1
   *  here, since the slow path doesn't visually composite vector
   *  shapes yet — they'll still render through Fabric in their
   *  normal z-order). The compositor passes the target as a
   *  full-canvas element; `paintLeaf` should NOT clear it (multiple
   *  leaves accumulate). */
  paintLeaf: (target: HTMLCanvasElement, layerId: string) => void;
  /** Look up the backing canvas the adjustment's display Fabric
   *  object wraps. The compositor writes the post-adjustment
   *  pixels here; caller is responsible for marking the wrapping
   *  Fabric object dirty + `requestRenderAll` AFTER `renderDocument`
   *  returns. Returning `null` skips that adjustment — useful when
   *  the adjustment's display object hasn't been created yet
   *  (e.g., during the first render after a new adjustment node
   *  was added). */
  getAdjustmentBacking: (layerId: string) => HTMLCanvasElement | null;
}

/** True if the document needs the custom compositor — any group
 *  whose blend / opacity isn't passthrough or any adjustment layer
 *  triggers the slow path. False = Tier 0-shaped document, plain
 *  Fabric rendering suffices.
 *
 *  Mask features (feather / density / invert / disable) no longer
 *  route through the slow path. They're materialized into the
 *  Fabric clipPath's effective alpha by
 *  `useFabricCanvas.materializeMaskClipPath`; Fabric's native
 *  `destination-in` clipPath compositing renders them correctly,
 *  and the slow-path compositor's `paintLeaf` calls
 *  `fabricObj.render(ctx)` which honors the clipPath, so adjustment
 *  clipping to a feathered/inverted/density-attenuated leaf also
 *  Just Works. */
export function requiresCustomComposite(doc: EditorDocument): boolean {
  for (const id of doc.rootIds) {
    if (nodeRequiresCustom(doc, id)) return true;
  }
  return false;
}

function nodeRequiresCustom(doc: EditorDocument, id: string): boolean {
  const node: LayerNode | undefined = doc.layers[id];
  if (!node) return false;
  if (node.type === 'adjustment') return true;
  if (node.type === 'group' && groupNeedsSlowPath(node)) return true;
  if (node.childIds && node.childIds.length > 0) {
    for (const childId of node.childIds) {
      if (nodeRequiresCustom(doc, childId)) return true;
    }
  }
  return false;
}

/** True when the group's blend / opacity isn't passthrough.
 *  Custom blend modes need offscreen compositing; opacity < 1
 *  needs flatten-then-fade. The UI doesn't surface group blend /
 *  opacity yet, so this always returns false in practice today. */
function groupNeedsSlowPath(node: LayerNode): boolean {
  const opacity = node.opacity ?? 1;
  const blendMode = node.blendMode ?? 'source-over';
  return opacity < 1 || blendMode !== 'source-over';
}

/** True when the document has slow-path features the compositor
 *  doesn't yet handle — currently just non-passthrough groups.
 *  Phase 2 implements adjustment layers in full; masks are now
 *  handled by `useFabricCanvas.materializeMaskClipPath` (Fabric's
 *  native clipPath alpha compositing). When non-passthrough groups
 *  land, this should drop to a hard `false`. */
export function hasUnimplementedSlowPath(doc: EditorDocument): boolean {
  for (const id in doc.layers) {
    const node = doc.layers[id];
    if (node.type === 'group' && groupNeedsSlowPath(node)) return true;
  }
  return false;
}

/** Leaf types the compositor paints into the running composite.
 *  Text and shape are excluded for Phase 2 — they render through
 *  Fabric in normal z-order, which is correct for the common case
 *  (adjustment layers above paint/image, with text/shape on top).
 *  When text/shape need to sit BELOW an adjustment, the compositor
 *  will need to rasterize them in tree order; deferred until a
 *  user asks. */
function isCompositableLeaf(node: LayerNode): boolean {
  return node.type === 'image' || node.type === 'paint';
}

/** Render the document. Fast path is a no-op (Fabric's own
 *  render loop already paints leaf layers correctly). Slow path
 *  walks the tree back-to-front, threading each rasterizable
 *  leaf through `paintLeaf` and each adjustment through
 *  `applyAdjustmentToCanvas`. Adjustment outputs are written to
 *  caller-owned backing canvases — caller must mark the
 *  wrapping Fabric objects dirty + `requestRenderAll` after this
 *  returns.
 *
 *  Adjustments marked `clipToBelow: true` are masked by the alpha
 *  of the immediately-preceding leaf in back-to-front order —
 *  Photoshop's clipping-mask semantics. Outside the clip
 *  region, the prior composite passes through unchanged.
 *
 *  No-op when slow-path features other than adjustments are
 *  present (mask feather/density/inverted/disabled, non-
 *  passthrough groups). Caller should `console.warn` once per
 *  document mutation in dev.
 *
 *  ── Performance characteristics ─────────────────────────────────
 *  Cost per pass is dominated by per-adjustment work, in this order
 *  (4K canvas / one BC adjustment ballpark):
 *   1. `getImageData` on the running composite — ~50ms (32MB CPU↔GPU
 *      transfer). Required input for the adjustment math.
 *   2. `applyAdjustmentToCanvas` per-pixel JS loop — ~100-400ms,
 *      kind-dependent. LUT-based kinds (B/C, Curves, Levels) are
 *      cheapest; HSL and Color Balance involve per-pixel HSL
 *      round-trips and run 3-4× slower.
 *   3. `putImageData` writing the result into the backing canvas —
 *      ~30ms.
 *   4. Clipping (if any) — a few drawImage calls via canvas2d
 *      composite operations; tens of ms even on 4K. Was previously
 *      a per-pixel JS blend at ~200ms; the composite-op refactor
 *      brought it under control.
 *
 *  So a single 4K adjustment costs ~200-500ms per recomposite; two
 *  stacked roughly doubles. Eye-toggle / opacity-slider / similar
 *  triggers a full recomposite (the entire adjustment stack must
 *  re-evaluate against the new visibility/transform). The rAF
 *  coalescing in the EditorPage effect prevents per-frame stacking,
 *  but each individual user action still incurs the per-adjustment
 *  cost.
 *
 *  Deferred optimizations worth picking up later:
 *   - Per-adjustment caching: stash the composite-below-this-
 *     adjustment per id; invalidate only when something below it
 *     actually changed. The current pass rebuilds from scratch even
 *     when only a layer ABOVE the adjustment moved.
 *   - GPU-accelerated adjustment math: for LUT-based kinds, a small
 *     WebGL shader (or canvas2d `filter` prop where the math maps
 *     cleanly) would replace the per-pixel JS loop entirely.
 *   - Downsampled interactive previews: keep full resolution only
 *     on commit / file save; use ~1024px during slider drags and
 *     eye-toggle. Stretch the result onto the backing canvas. */
export function renderDocument(opts: RenderPipelineOptions): void {
  if (!requiresCustomComposite(opts.document)) return;
  if (hasUnimplementedSlowPath(opts.document)) return;

  const composite = document.createElement('canvas');
  composite.width = opts.width;
  composite.height = opts.height;
  const compositeCtx = composite.getContext('2d', { willReadFrequently: true });
  if (!compositeCtx) return;

  // Back-to-front order. `toOrderedList` returns front-most first;
  // we reverse for paint order. Walking the flat (depth-first
  // flattened) list directly works because passthrough groups
  // contribute no compositing of their own — their children just
  // render in their tree z-order, which is what we get from the
  // flattened walk.
  const order = backToFrontOrder(opts.document);

  // Pre-walk to resolve clip targets. Tree-aware: each clipped
  // adjustment clips to the next visible non-adjustment SIBLING in
  // front-most-first sibling order (i.e. the layer immediately
  // below it in the LayersPanel within its own scope). When that
  // sibling is a group, the clip mask is the union alpha of every
  // visible rasterizable descendant of the group — so an
  // adjustment placed above a group ends up scoped to "everything
  // inside that group", which is the user-intent fix for the
  // group-as-target case.
  //
  // Resolution returns an ORDERED list of leaf ids to paint into
  // the mask canvas (back-to-front within the group). Empty list
  // = no valid clip target (e.g. adjustment is the bottom-most
  // sibling); the compositor falls back to unclipped behavior in
  // that case.
  const clipTargets = new Map<string, string[]>();
  for (const id in opts.document.layers) {
    const adj = opts.document.layers[id];
    if (adj.type !== 'adjustment' || !adj.clipToBelow || !adj.visible) continue;
    const leafIds = resolveClipTargetLeaves(opts.document, adj);
    if (leafIds.length > 0) clipTargets.set(adj.id, leafIds);
  }

  for (const node of order) {
    if (!node.visible) continue;
    // Group nodes themselves contribute nothing; their children
    // appear earlier/later in `order` and are handled there.
    if (node.type === 'group') continue;

    if (node.type === 'adjustment' && node.adjustment) {
      if (!isImplementedKind(node.adjustment.kind)) continue;
      const backing = opts.getAdjustmentBacking(node.id);
      if (!backing) continue;
      // Sizing mismatch is the caller's bug — the backing canvas
      // is created at the canvas dimensions and should stay that
      // way. Skip silently rather than producing wrong pixels.
      if (backing.width !== opts.width || backing.height !== opts.height) continue;
      const sourcePixels = compositeCtx.getImageData(0, 0, opts.width, opts.height);
      const applied = applyAdjustmentToCanvas(sourcePixels, backing, node.adjustment);
      if (!applied) continue;

      const clipLeafIds = clipTargets.get(node.id);
      if (clipLeafIds && clipLeafIds.length > 0) {
        // Clipped adjustment: paint every leaf in the clip target
        // into a fresh mask canvas (their union alpha forms the
        // clip region for groups), then blend the adjusted result
        // with the pre-adjustment composite using that alpha.
        // Outside the clip region (alpha == 0) the source shows
        // through; inside, the full adjustment lands.
        //
        // Implementation uses canvas2d composite operations rather
        // than per-pixel JS. A 4K canvas's per-pixel blend was the
        // dominant cost in the slow-path (multi-second eye-toggle
        // lag); GPU-accelerated `destination-in` / `destination-out`
        // / `source-over` brings it down to a few drawImage calls.
        const clipMask = document.createElement('canvas');
        clipMask.width = opts.width;
        clipMask.height = opts.height;
        for (const lid of clipLeafIds) {
          opts.paintLeaf(clipMask, lid);
        }
        maskAdjustmentByAlphaComposite(backing, composite, clipMask);
      }

      // The adjustment's output is now the running composite for
      // any further adjustments above it.
      compositeCtx.clearRect(0, 0, opts.width, opts.height);
      compositeCtx.drawImage(backing, 0, 0);
      continue;
    }

    if (isCompositableLeaf(node)) {
      opts.paintLeaf(composite, node.id);
    }
  }
}

/** Resolve a clipped adjustment's mask source.
 *
 *  Walks the adjustment's siblings (front-most first) past itself
 *  to find the next visible non-adjustment sibling — that's the
 *  clip target. If the target is a single rasterizable leaf, the
 *  result is `[targetId]`. If the target is a group, the result is
 *  the back-to-front-ordered ids of every visible `image`/`paint`
 *  descendant — the compositor paints all of them into one mask
 *  canvas to form the union alpha.
 *
 *  Returns an empty array when no valid clip target exists (e.g.
 *  the adjustment is the bottom-most sibling). Callers MUST treat
 *  empty as "fall back to unclipped" rather than "clip to zero
 *  alpha" — otherwise the adjustment would silently vanish. */
function resolveClipTargetLeaves(doc: EditorDocument, adj: LayerNode): string[] {
  const parentId = adj.parentId;
  const siblings = parentId === null ? doc.rootIds : (doc.layers[parentId]?.childIds ?? []);
  const idx = siblings.indexOf(adj.id);
  if (idx < 0) return [];
  // Front-most-first ordering: siblings AFTER the adjustment are
  // BELOW it visually. Walk forward to find the next valid clip
  // sibling.
  let targetId: string | null = null;
  for (let i = idx + 1; i < siblings.length; i++) {
    const sib = doc.layers[siblings[i]];
    if (!sib || !sib.visible) continue;
    if (sib.type === 'adjustment') continue;
    targetId = sib.id;
    break;
  }
  if (!targetId) return [];
  const target = doc.layers[targetId];
  if (!target) return [];
  if (target.type === 'image' || target.type === 'paint') return [targetId];
  if (target.type === 'group') {
    // `traverseDescendants` yields the starting node first (the
    // group), then descendants front-most-first. We filter to just
    // visible rasterizable leaves; the iteration order doesn't
    // matter for the mask (alpha union is associative).
    const out: string[] = [];
    for (const descendant of traverseDescendants(doc, targetId)) {
      if (!descendant.visible) continue;
      if (descendant.type === 'image' || descendant.type === 'paint') {
        out.push(descendant.id);
      }
    }
    return out;
  }
  // text/shape/empty/nested-adjustment as the immediate sibling:
  // no rasterizable surface to clip against. Treat as no clip
  // target — adjustment runs unclipped against the running
  // composite.
  return [];
}

/** Blend the adjusted pixels in `backing` with `sourceCanvas` using
 *  `clipMask`'s alpha as the per-pixel mix weight. Replaces
 *  `backing`'s pixels in place. Result is mathematically the lerp
 *  `out = source * (1 - mask.alpha) + adjusted * mask.alpha`.
 *
 *  Uses canvas2d composite operations exclusively — no per-pixel
 *  JavaScript. `destination-in` keeps the adjusted pixels where the
 *  mask is opaque; `destination-out` punches the source where the
 *  mask is opaque; `source-over` recombines them. Each step is one
 *  drawImage call that the browser dispatches to the GPU when
 *  possible, so on a 4K canvas this completes in single-digit ms
 *  versus hundreds of ms for the per-pixel JS loop it replaced.
 *
 *  All three inputs must be the same size; sizing checks are the
 *  caller's responsibility. */
function maskAdjustmentByAlphaComposite(
  backing: HTMLCanvasElement,
  sourceCanvas: HTMLCanvasElement,
  clipMask: HTMLCanvasElement,
): void {
  const w = backing.width;
  const h = backing.height;
  // Snapshot the adjusted pixels — `backing` is about to be
  // rewritten and we need the pre-rewrite pixels to recombine.
  const adjustedTemp = document.createElement('canvas');
  adjustedTemp.width = w;
  adjustedTemp.height = h;
  const adjCtx = adjustedTemp.getContext('2d');
  if (!adjCtx) return;
  adjCtx.drawImage(backing, 0, 0);
  // Keep only the part of `adjustedTemp` inside the clip region.
  adjCtx.globalCompositeOperation = 'destination-in';
  adjCtx.drawImage(clipMask, 0, 0);

  // Build a sourceTemp that contains source pixels OUTSIDE the
  // clip region (source with the clip region punched out).
  const sourceTemp = document.createElement('canvas');
  sourceTemp.width = w;
  sourceTemp.height = h;
  const srcCtx = sourceTemp.getContext('2d');
  if (!srcCtx) return;
  srcCtx.drawImage(sourceCanvas, 0, 0);
  srcCtx.globalCompositeOperation = 'destination-out';
  srcCtx.drawImage(clipMask, 0, 0);

  // Recompose into `backing`. `sourceTemp` covers outside-clip,
  // `adjustedTemp` covers inside-clip — they tile together with
  // straight `source-over` since their alpha regions don't overlap.
  const ctx = backing.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(sourceTemp, 0, 0);
  ctx.drawImage(adjustedTemp, 0, 0);
}

/** Flatten the tree depth-first and reverse for back-to-front
 *  paint order. Inlined rather than importing `toOrderedList` +
 *  `.reverse()` so the slow-path costs one less allocation and
 *  the traversal stays explicit at the call site. */
function backToFrontOrder(doc: EditorDocument): LayerNode[] {
  const front: LayerNode[] = [];
  const stack: string[] = [];
  for (let i = doc.rootIds.length - 1; i >= 0; i--) {
    stack.push(doc.rootIds[i]);
  }
  while (stack.length > 0) {
    const id = stack.pop()!;
    const node = doc.layers[id];
    if (!node) continue;
    front.push(node);
    if (node.type === 'group' && node.childIds && node.childIds.length > 0) {
      for (let i = node.childIds.length - 1; i >= 0; i--) {
        stack.push(node.childIds[i]);
      }
    }
  }
  // `front` is front-most-first (same as `toOrderedList`); reverse
  // for back-to-front paint order.
  return front.reverse();
}
